// src/Hooks/useProductQuery.ts
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchFilteredProducts,
  fetchAvailableTags,
  fetchFeaturedProducts,
  ProductFilters,
  PaginatedProducts,
  Product,
} from '../Services/productService';
import { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';

// ─────────────────────────────────────────────────────────────────────────────
// QUERY KEYS
//
// Centralised key factory — same pattern as BRAND_KEYS / CATEGORY_QUERY_KEYS.
//
//   PRODUCT_KEYS.all                → invalidates EVERYTHING under 'products'
//   PRODUCT_KEYS.filtered(filters)  → one specific filter combo (infinite list)
//   PRODUCT_KEYS.tags()             → the available-tags list
// ─────────────────────────────────────────────────────────────────────────────
export const PRODUCT_KEYS = {
  all: ['products'] as const,
  filtered: (filters: ProductFilters) =>
    [
      'products',
      'filtered',
      filters.categoryId ?? null,
      filters.subcategoryId ?? null,
      filters.brandId ?? null,
      filters.searchQuery ?? null,
      (filters.tags ?? []).join(','),
      filters.sortBy ?? 'newness',
    ] as const,
  tags: () => ['products', 'tags'] as const,
  featured: () => ['products', 'featured'] as const,
  // HomeShop uses a plain query (no infinite scroll — Load More navigates to /shop)
  homeShop: (filters: ProductFilters) =>
    [
      'products',
      'homeShop',
      filters.categoryId ?? null,
      filters.searchQuery ?? null,
      (filters.tags ?? []).join(','),
      filters.sortBy ?? 'newness',
    ] as const,
};

// ─────────────────────────────────────────────────────────────────────────────
// Page param type
//
// React Query's useInfiniteQuery passes the return value of `getNextPageParam`
// as `pageParam` into `queryFn`. We use the Firestore cursor doc as that param.
//
//   null      → fetch the first page (initialPageParam)
//   undefined → React Query's conventional "no more pages" sentinel; returned
//               by getNextPageParam when hasMore is false. Must be in the union
//               so TypeScript accepts the return type of getNextPageParam.
// ─────────────────────────────────────────────────────────────────────────────
type PageParam = QueryDocumentSnapshot<DocumentData> | null | undefined;

// ─────────────────────────────────────────────────────────────────────────────
// useFilteredProductsInfinite
//
// Infinite-scroll query for the Shop page.
//
// WHY useInfiniteQuery:
//   - The Shop uses "Load More" (append) semantics, not cursor-based numbered
//     pages. useInfiniteQuery is built for exactly this: each page's result is
//     stored separately, and `data.pages` is a flat array of PaginatedProducts
//     that you concat for rendering.
//
// CACHE KEY:
//   - Keyed by the full filter object. Changing ANY filter (category, brand,
//     search, sort, tags) busts the cache and re-fetches from page 1.
//
// CURSOR HANDLING:
//   - Firestore cursors (QueryDocumentSnapshot) cannot be serialised into the
//     query key, so they are NOT part of the key. Instead:
//     - `queryFn` receives `pageParam` (the cursor doc from the previous page).
//     - `getNextPageParam` extracts the lastDoc from the last fetched page and
//       returns it as the cursor for the next fetch.
//     - When filters change the key changes → React Query starts a fresh
//       infinite query from page 1 (pageParam = undefined → null).
//
// staleTime:
//   - 2 minutes. Product stock levels change, so we keep a shorter window than
//     brands/categories (which change much less often).
//
// placeholderData: (prev) => prev
//   - Keeps the previous filter's results visible while the new filter is
//     loading, preventing a full blank-screen flash on every filter change.
// ─────────────────────────────────────────────────────────────────────────────
export const useFilteredProductsInfinite = (filters: ProductFilters) => {
  return useInfiniteQuery<
    PaginatedProducts,
    Error,
    { pages: PaginatedProducts[] },
    ReturnType<typeof PRODUCT_KEYS.filtered>,
    PageParam
  >({
    queryKey: PRODUCT_KEYS.filtered(filters),
    queryFn: ({ pageParam }) =>
      fetchFilteredProducts(filters, pageParam ?? null),
    initialPageParam: null as PageParam,
    getNextPageParam: (lastPage: PaginatedProducts): PageParam => {
      // If the service says there are more products AND returned a cursor doc,
      // return that doc as the next pageParam. Otherwise signal "no more pages"
      // by returning undefined (React Query convention).
      if (lastPage.hasMore && lastPage.lastDoc) {
        return lastPage.lastDoc;
      }
      return undefined;
    },
    staleTime: 1000 * 60 * 2,           // 2 minutes — products change more often
    placeholderData: (prev: any) => prev, // show previous results while loading new filter
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// useAvailableTags
//
// Fetches the deduplicated tag list from Firestore (scans up to 1 000 products
// client-side — unchanged from the original fetchAvailableTags service call).
//
// Previously this was triggered once via `initializedRef` inside a useEffect.
// Moving it to React Query gives us:
//   - Automatic deduplication across mounts (tags are fetched once per session)
//   - A long staleTime (10 min) because tags rarely change
//   - Zero boilerplate loading/error state in the component
// ─────────────────────────────────────────────────────────────────────────────
export const useAvailableTags = () => {
  return useQuery<string[], Error>({
    queryKey: PRODUCT_KEYS.tags(),
    queryFn: fetchAvailableTags,
    staleTime: 1000 * 60 * 10,  // 10 minutes — tag lists are fairly stable
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// useFeaturedProducts
//
// Fetches the featured products list shown in sidebars / home widgets.
//
// Previously triggered manually inside a useEffect with local loading/products
// state. React Query gives us:
//   - A single shared fetch — if FeaturedProducts mounts in multiple places
//     (e.g. product detail sidebar + homepage), only one Firestore read fires.
//   - A 5-minute stale window — featured products change infrequently and the
//     list is small, so a moderate cache window is safe.
//   - Automatic invalidation via useInvalidateProducts() after admin writes,
//     since the key starts with ['products'] which PRODUCT_KEYS.all covers.
// ─────────────────────────────────────────────────────────────────────────────
export const useFeaturedProducts = (maxProducts: number = 12) => {
  return useQuery<Product[], Error>({
    queryKey: PRODUCT_KEYS.featured(),
    queryFn: () => fetchFeaturedProducts(maxProducts),
    staleTime: 1000 * 60 * 5,  // 5 minutes — featured list changes infrequently
  });
};


// ─────────────────────────────────────────────────────────────────────────────
// useHomeShopProducts
//
// Plain useQuery (not infinite) for the HomeShop component on the homepage.
//
// WHY not useInfiniteQuery:
//   HomeShop's "Load More" button navigates to /shop rather than appending
//   more products. There is only ever one page of results needed, so a plain
//   useQuery is simpler and correct.
//
// CACHE KEY:
//   Keyed by category, search, tags, sortBy. Changing any filter busts the
//   cache and re-fetches automatically. Sits under ['products', 'homeShop']
//   so useInvalidateProducts (targeting PRODUCT_KEYS.all) still covers it.
//
// staleTime: 2 minutes — stock levels can change frequently.
// placeholderData: keeps previous results visible during filter transitions.
// ─────────────────────────────────────────────────────────────────────────────
export const useHomeShopProducts = (filters: ProductFilters) => {
  return useQuery<PaginatedProducts, Error>({
    queryKey: PRODUCT_KEYS.homeShop(filters),
    queryFn: () => fetchFilteredProducts(filters, null),
    staleTime: 1000 * 60 * 2,
    placeholderData: (prev: any) => prev,
  });
};

// ─────────────────────────────────────────────────────────────────────────────
//
// Utility hook for invalidating all product queries (e.g. after an admin
// creates/edits/deletes a product). Follows the same pattern as
// useInvalidateBrands and useInvalidateCategories.
// ─────────────────────────────────────────────────────────────────────────────
export const useInvalidateProducts = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: PRODUCT_KEYS.all });
};